import {Box, Flex, Text} from "@chakra-ui/react";
import React from "react";
import {Product} from "../../../shared/entities/Product/Product";
import EditButton from "../../../components/Buttons/EditButton";
import ImageCustom from "../../../components/Image/ImageCustom";
import DragAndDropBox from "../../../components/DragAndDrop/DragAndDropBox";
import ProductVideo from "../ProductVideo";
import {generalBox} from "../../../theme/foundations/breakpoints";
import {CardBody} from "../../../components/Card/CardBody";
import {WebPUtils} from "../../../shared/utilities/WebPUtils";
import CustomLink from "../../../components/Link/CustomLink";
import AllowedAccess, {hasPermission} from "../../../features/Permission/AllowedAccess";
import {productEditPermission} from "../../permissions";

interface CategorySectionProps {
    product: Product;
    isMobile?: boolean;
}

/** Approx. chars that fit in one line at 2rem in the category title area; tune if needed. */
const CATEGORY_NAME_SINGLE_LINE_MAX_LENGTH = 12;

/** Soft line-break marker in product name (hidden on display). Example: `CLEANING|CAR WASH SUPPLIES` */
const CATEGORY_NAME_LINE_BREAK = "|";

const formatCategoryName = (name: string) =>
    name.split(CATEGORY_NAME_LINE_BREAK).join("\n");

const CategoryGeneralItem: React.FC<CategorySectionProps> = ({product, isMobile = false}) => {
    const rawName = product.name ?? "";
    const displayName = formatCategoryName(rawName);
    const hasExplicitBreak = rawName.includes(CATEGORY_NAME_LINE_BREAK);
    const lengthForSizing = rawName.split(CATEGORY_NAME_LINE_BREAK).join("").length;
    const isMultiLineName = hasExplicitBreak || lengthForSizing > CATEGORY_NAME_SINGLE_LINE_MAX_LENGTH;

    return (
        <Flex w={"100%"} height={"100%"}>
            <AllowedAccess permissionKey={productEditPermission} localPermission={!isMobile}>
                <DragAndDropBox item={product} width={"5%"} orderByZone={true}/>
            </AllowedAccess>
            <CardBody backgroundColor={product.grayBackground ? "#F3F4F6" : undefined}>
                <DragAndDropBox item={product} isDraggable={hasPermission(productEditPermission)}>
                    {product.id !== -1 && <EditButton item={product}/>}
                    <CustomLink href={WebPUtils.getProductLink(product)}>
                        <Flex p={6} height={generalBox.category.heightTitle}>
                            <Flex alignItems="center">
                                <Text
                                    fontSize={isMultiLineName ? "24px" : "2rem"}
                                    fontWeight={"md"}
                                    letterSpacing={"-0.04rem"}
                                    lineHeight={isMultiLineName ? "110%" : "2rem"}
                                    whiteSpace="pre-line"
                                    cursor={"pointer"}
                                >
                                    {displayName}
                                </Text>
                                <Box pl={3}>
                                    <ProductVideo video={product.video[0]}/>
                                </Box>
                            </Flex>
                        </Flex>
                    </CustomLink>
                    <ImageCustom elem={product} currentSize={"general"} sizeBackend={"GENERAL"} isMobile={isMobile}/>
                </DragAndDropBox>
            </CardBody>
        </Flex>
    );
};

export default CategoryGeneralItem;
